import { changetheme } from '../../../../store/layout/layout-action';
import type { LayoutState } from '../../../../store/layout/layout-reducers';
import { getLayoutColor } from '../../../..//store/layout/layout-selector';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  inject,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
  type OnInit,
  type TemplateRef,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import {
  NgbDropdownModule,
  NgbModal,
  NgbModalModule,
  NgbOffcanvasModule,
} from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { appData, languages } from '../../common/data';
import { SimplebarAngularModule } from 'simplebar-angular';
import { splitArray } from '../../helper/utils';
import { LogoBoxComponent } from '../logo-box/app-logo-box';
import { currency } from '../../common/constants';
import { logout } from '../../../../store/authentication/authentication.actions';
import { SearchComponent } from '../search/search.component';
import { ToasterMessageService } from '../../services/topbar-toaster/toaster-message.service';
import { CommonModule } from '@angular/common';
import { GlobalService } from '../../services/common/global.service';
import { AvatarUtils } from '../../services/utils/avatar-utils';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IncomingCallNotificationComponent } from '../incoming-call-notification/incoming-call-notification.component';
import { TwilioService } from '../../../patient-on-boarding-portal/service/twilio.service';
import { Call } from '@twilio/voice-sdk';
import { Subscription } from 'rxjs';
import { CommunicationLogsService } from '../../../care-manager-portal/components/communication-logs/services/communication-logs.service';
import { AiChatComponent } from '../ai-chat/ai-chat.component';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [
    NgbOffcanvasModule,
    NgbDropdownModule,
    SimplebarAngularModule,
    NgbModalModule,
    LogoBoxComponent,
    CommonModule,
    IncomingCallNotificationComponent,
    AiChatComponent,
  ],
  templateUrl: './topbar.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  styleUrls: ['./topbar.component.css'],
})
export class TopbarComponent implements OnInit, OnDestroy {
  currency = currency;

  currentMessage: string = '';
  currentClass: string = '';
  messageTextClass: string = '';
  isVisible: boolean = true;
  icon: string = '';
  languageList = languages;
  appsChunks = splitArray(appData, 3);
  avatar: SafeHtml | null = null;
  isAvatar: boolean = false;

  // Twilio status properties
  deviceRegistered: boolean = true;
  twilioStatusBg: string = '#e0f8eb';
  twilioStatusText: string = '#31ce77';
  twilioStatusIcon: string = 'ti ti-circle-check-filled';

  showTwilioStatus: boolean = false;
  callsInQueue: number = 0;
  queueList: any[] = [];

  userRole: string | null = null;
  aichat: boolean = false;

  @Output() settingsButtonClicked = new EventEmitter();
  @Output() mobileMenuButtonClicked = new EventEmitter();

  private modalService = inject(NgbModal);
  private sanitizer = inject(DomSanitizer);
  private messageService = inject(ToasterMessageService);
  router = inject(Router);
  store = inject(Store);
  private globalService = inject(GlobalService);
  private twilioService = inject(TwilioService);
  private communicationService = inject(CommunicationLogsService);
  private ngZone = inject(NgZone);
  

  userSettings!: any;
  color!: string;
  activeConnection: any = null;
  private isStartingNextCall: boolean = false;
  private nextCallWaitSub: Subscription | null = null;
  private nextCallWaitTimeout: any = null;
  @ViewChild(IncomingCallNotificationComponent)
  incomingCallNotificationComponent?: IncomingCallNotificationComponent;
  open(value: string) {}

  ngOnInit(): void {
    this.userSettings = JSON.parse(this.globalService.getUserSettings());
    let thumbnail = this.userSettings.userInfo.imagePath;

    // Check userRole from localStorage
    const userRole = localStorage.getItem('userRole');
    this.userRole = userRole;
    this.showTwilioStatus =
      userRole === 'patientOnBoarding' || userRole == 'care';

    this.updateTwilioStatusFromStorage();

    // Listen for localStorage changes (from other tabs/windows)
    window.addEventListener('storage', (event) => {
      if (event.key === 'deviceRegistered' || event.key === 'userRole') {
        this.updateTwilioStatusFromStorage();
        const userRole = localStorage.getItem('userRole');
        this.userRole = userRole;
        this.showTwilioStatus =
          userRole === 'patientOnBoarding' || userRole == 'care';
      }
    });

    // Runs outside Angular's zone so this 1s poll doesn't trigger a full
    // app-wide change detection tick every second (steals main-thread time
    // from the WebRTC audio pipeline during an active call, causing jitter).
    // updateTwilioStatusFromStorage() re-enters the zone itself only when
    // the status actually changes.
    this.ngZone.runOutsideAngular(() => {
      setInterval(() => this.updateTwilioStatusFromStorage(), 1000);
    });

    // Optionally, poll for changes in the same tab (if deviceRegistered is set in this tab)
    // setInterval(() => this.updateTwilioStatusFromStorage(), 2000);

    if (thumbnail == null || thumbnail == '' || thumbnail == 'undefined') {
      const avatarHtml = AvatarUtils.getInitialsWithColorForBar(
        this.userSettings.userInfo.first_name,
        this.userSettings.userInfo.last_name,
        false,
      );
      this.avatar = this.sanitizer.bypassSecurityTrustHtml(avatarHtml);
      this.isAvatar = true;
    }
  
    this.store.select('layout').subscribe((data: LayoutState) => {
      this.color = data.LAYOUT_THEME;
    });

    this.getCounts();
    setInterval(() => {
      this.getCounts();
    }, 90000); 
  }
  goToCommunicationLogs() {
    this.router.navigate(['/dashboard/caremanager/communication-logs'], {
      queryParams: { tab: 3 }
    });
  }
  ngOnDestroy(): void {
    if (this.nextCallWaitSub) {
      this.nextCallWaitSub.unsubscribe();
      this.nextCallWaitSub = null;
    }
    if (this.nextCallWaitTimeout) {
      clearTimeout(this.nextCallWaitTimeout);
      this.nextCallWaitTimeout = null;
    }
  }
  patientInP1Counts:number =0
  getCounts() {
    this.communicationService.getCounts().subscribe({
      next: (res: any) => {
        this.patientInP1Counts = res.p1_count
      },
      error: (err: any) => { }
    })
  }
  getInitials(firstName: string, lastName: string): string {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last;
  }
  updateTwilioStatusFromStorage() {
    const deviceRegisteredStr = localStorage.getItem('deviceRegistered');
    const nextDeviceRegistered = deviceRegisteredStr === 'true';

    // Only re-enter Angular's zone (triggering change detection) when the
    // status actually flips — avoids a CD tick on every 1s poll when nothing
    // changed, which is the steady-state case for the entire duration of a call.
    if (nextDeviceRegistered === this.deviceRegistered) {
      return;
    }

    this.ngZone.run(() => {
      this.deviceRegistered = nextDeviceRegistered;
      if (this.deviceRegistered) {
        this.twilioStatusBg = '#e0f8eb';
        this.twilioStatusText = '#31ce77';
        this.twilioStatusIcon = 'ti ti-circle-check-filled';
      } else {
        this.twilioStatusBg = '#FDE4E3';
        this.twilioStatusText = '#F34943';
        this.twilioStatusIcon = 'ti ti-xbox-x-filled';
      }
    });
  }

  settingMenu() {
    this.settingsButtonClicked.emit();
  }

  openAiChat(): void {
    this.aichat = true;
  }

  closeAiChat(): void {
    this.aichat = false;
  }

  toggleMobileMenu() {
    this.mobileMenuButtonClicked.emit();
  }

  changeTheme() {
    const color = document.documentElement.getAttribute('data-bs-theme');
    if (color == 'light') {
      this.store.dispatch(changetheme({ color: 'dark' }));
    } else {
      this.store.dispatch(changetheme({ color: 'light' }));
    }
    this.store.select(getLayoutColor).subscribe((color) => {
      document.documentElement.setAttribute('data-bs-theme', color);
    });
  }

  logout() {
    this.store.dispatch(logout());
  }

  close() {
    this.isVisible = false;
  }

  showQueueDropdown = false;

  toggleQueueDropdown() {
    this.showQueueDropdown = !this.showQueueDropdown;
  }

  onQueueListFetched(queueList: any[]): void {
    this.queueList = Array.isArray(queueList) ? queueList : [];
    this.callsInQueue = this.queueList.length;
  }

  getLongestWaitTime(): string {
    if (!this.queueList || this.queueList.length === 0) return '00:00';
    const now = new Date();
    const oldest = this.queueList.reduce((min, item) => {
      const itemDate = new Date(item.createdOn);
      return itemDate < new Date(min.createdOn) ? item : min;
    }, this.queueList[0]);
    const created = new Date(oldest.createdOn);
    let diff = Math.floor((now.getTime() - created.getTime()) / 1000);
    if (diff < 0) diff = 0;
    const h = Math.floor(diff / 3600)
      .toString()
      .padStart(2, '0');
    const m = Math.floor((diff % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const s = Math.floor(diff % 60)
      .toString()
      .padStart(2, '0');
    return `${h}:${m}:${s}`;
  }

  getOldestQueueNumber(): string {
    if (!this.queueList || this.queueList.length === 0) return '';
    const oldest = this.queueList.reduce((min, item) => {
      const itemDate = new Date(item.createdOn);
      return itemDate < new Date(min.createdOn) ? item : min;
    }, this.queueList[0]);
    return oldest.patientNumber || '';
  }

  getOldestQueueName(): string {
    if (!this.queueList || this.queueList.length === 0) return '';
    const oldest = this.queueList.reduce((min, item) => {
      const itemDate = new Date(item.createdOn);
      return itemDate < new Date(min.createdOn) ? item : min;
    }, this.queueList[0]);
    return oldest.patientName || '';
  }

  pickUpNextCall(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!this.queueList || this.queueList.length === 0) return;

    const oldest = this.queueList.reduce((min, item) => {
      const itemDate = new Date(item.createdOn);
      return itemDate < new Date(min.createdOn) ? item : min;
    }, this.queueList[0]);

    this.startNextCall(oldest);
    this.showQueueDropdown = false;
  }

  private startNextCall(data: any): void {
    if (this.isStartingNextCall) {
      return;
    }
    this.isStartingNextCall = true;

    const runNextCall = () => {
      if (this.nextCallWaitSub) {
        this.nextCallWaitSub.unsubscribe();
        this.nextCallWaitSub = null;
      }
      if (this.nextCallWaitTimeout) {
        clearTimeout(this.nextCallWaitTimeout);
        this.nextCallWaitTimeout = null;
      }
      this.makeCall(data);
    };

    try {
      const twilioServiceDisconnect = (this.twilioService as any).disconnectCall?.bind(
        this.twilioService,
      );
      const sharedCall = (this.twilioService as any).getActiveCall?.();

      if (!sharedCall) {
        runNextCall();
        return;
      }

      if (twilioServiceDisconnect) {
        twilioServiceDisconnect();
      } else if (sharedCall?.disconnect) {
        sharedCall.disconnect();
      } else if (this.activeConnection?.disconnect) {
        this.activeConnection.disconnect();
      }
    } catch (err) {
      console.warn('Failed to disconnect current call before pickup:', err);
      this.isStartingNextCall = false;
      return;
    }

    this.nextCallWaitSub = (this.twilioService as any).activeCall$?.subscribe(
      (call: Call | undefined) => {
        if (!call) {
          runNextCall();
        }
      },
    );

    this.nextCallWaitTimeout = setTimeout(() => {
      runNextCall();
    }, 4000);
  }

  private async makeCall(callData: any): Promise<void> {
    try {
      const cognitoID = localStorage.getItem('cognitoID');
      const detail = `${callData.patientNumber}/0/${callData.patientId || 0}/${cognitoID}/incomingCall/incoming/${callData.callSid}/${callData.twilioNumber}`;

      const call = await this.twilioService.makeCall(
        { detail },
        callData.patientId,
      );

      if (call) {
        this.activeConnection = call;
        try {
          this.twilioService.setActiveCall(call);
        } catch (e) {}

        this.incomingCallNotificationComponent?.openCallModalFromTopbar();

        call.on('disconnect', () => {
          this.activeConnection = null;
          try {
            this.twilioService.setActiveCall(undefined);
          } catch (e) {}
        });
      }
    } finally {
      this.isStartingNextCall = false;
    }
  }
}
